import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Bell, Headset, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useSupportUnread from '@/hooks/useSupportUnread';
import useNotificationUnread from '@/hooks/useNotificationUnread';
import useManagedClusters from '@/hooks/useManagedClusters';

/**
 * The African Farm header on every role's home: a green band with the
 * leaf mark, the wordmark and a welcome line on the left, and profile,
 * messages and notifications on the right. The band runs edge to edge
 * (it cancels the page's padding) and the page content slides up over
 * its bottom edge on a rounded sheet (see HomeTop).
 *
 * The band colour is the logo's own background, so the mark sits on it
 * without a visible square.
 */
export const BRAND_GREEN = '#337D31';

export default function BrandHeader({ className = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const displayName = _firstName(user?.full_name);
  const unreadSupport = useSupportUnread();
  const unreadNotifications = useNotificationUnread();
  const isClusterManager = useManagedClusters(user).length > 0;

  return (
    <header
      className={`-mx-4 -mt-3 px-4 pt-4 pb-9 flex items-center justify-between gap-2 text-white ${className}`}
      style={{ backgroundColor: BRAND_GREEN }}
      data-testid="brand-header"
    >
      <div className="flex items-center gap-2 min-w-0">
        <img
          src="/assets/brand/logo-mark.png"
          alt={t('brand.altText', {
            defaultValue: 'African Farm — Smart Farming Solutions',
          })}
          className="w-12 h-12 flex-shrink-0"
          loading="eager"
          decoding="async"
        />
        <div className="leading-tight min-w-0">
          <p className="font-heading text-[22px] font-bold text-white whitespace-nowrap">
            {t('brand.name.first')} {t('brand.name.second')}
          </p>
          {displayName ? (
            <p
              className="text-sm font-semibold text-white/90 truncate"
              data-testid="brand-header-welcome"
            >
              {t('common.welcomeName', {
                name: displayName,
                defaultValue: 'Welcome, {{name}}',
              })}
            </p>
          ) : (
            <p className="text-sm text-white/80 truncate">{t('brand.tagline')}</p>
          )}
          {/* Farmers the support team made manager of a cluster (PR 71). */}
          {isClusterManager && <ClusterManagerBadge className="mt-1" />}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Avatar — tappable circle that routes to /profile. Falls
            back to a neutral silhouette when the user hasn't set a
            photo yet (PR 15). */}
        <button
          type="button"
          aria-label={t('common.profile', { defaultValue: 'Profile' })}
          onClick={() => navigate('/profile')}
          className="w-12 h-12 rounded-full bg-white/15 overflow-hidden flex items-center justify-center ring-2 ring-white/70 hover:ring-white transition-colors"
          data-testid="brand-header-avatar"
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-white" />
          )}
        </button>
        {/* Support: chat with the African Farm team. Labelled, because a
            headset alone isn't obvious to every farmer. */}
        <button
          type="button"
          aria-label={unreadSupport
            ? t('supportChat.buttonUnread', { count: unreadSupport })
            : t('supportChat.button')}
          onClick={() => navigate('/support/chat')}
          className="relative flex flex-col items-center px-1 pt-1 text-white hover:text-farm-gold-light transition-colors"
          data-testid="brand-header-support"
        >
          <Headset className="w-9 h-9" strokeWidth={2} />
          <span className="text-[11px] font-semibold leading-none mt-0.5">{t('supportChat.button')}</span>
          <CountBadge count={unreadSupport} testId="brand-header-support-badge" />
        </button>
        <button
          type="button"
          aria-label={unreadNotifications
            ? t('notifications.titleUnread', { count: unreadNotifications })
            : t('common.notifications', { defaultValue: 'Notifications' })}
          onClick={() => navigate('/notifications')}
          className="relative p-1 text-white hover:text-farm-gold-light transition-colors"
          data-testid="brand-header-bell"
        >
          <Bell className="w-9 h-9" strokeWidth={2} />
          <CountBadge count={unreadNotifications} testId="brand-header-bell-badge" />
        </button>
      </div>
    </header>
  );
}

export function ClusterManagerBadge({ className = '' }) {
  const { t } = useTranslation();
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-[#FFC107] text-farm-forest text-xs font-extrabold px-2.5 py-0.5 ${className}`}
          data-testid="cluster-manager-badge">
      <BadgeCheck className="w-4 h-4" /> {t('clusterManager.badge')}
    </span>
  );
}

function CountBadge({ count, testId }) {
  if (!count) return null;
  return (
    <span
      className="absolute top-0 right-0 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
      data-testid={testId}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// A phone-number-registered user's full_name can be anything they
// typed at signup; keep the header compact by using just the first
// token, but fall back to the whole string when there's no space.
function _firstName(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  const space = trimmed.indexOf(' ');
  return space > 0 ? trimmed.slice(0, space) : trimmed;
}
